import { fetchOptionalLegacyProps } from '../config';
import { HttpError } from '../customErrors';
import { captureException } from '../sentry';

const baseUrl = () => fetchOptionalLegacyProps()?.base_url || '/';
export const apiUrl = (path) => `${baseUrl()}api/${path}`;
export const isApplicationJson = (contentType) => contentType && contentType.indexOf('application/json') !== -1;

export const contentTypeUrlEnc = 'application/x-www-form-urlencoded; charset=UTF-8';

const getUrlEncField = (key, val) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;

/**
 *
 * @param {array} data Array of array. On form [[key1, val1], [key2, val2]]
 * @return {string}
 * */
export function urlEncodeData(data) {
  return data
    .filter(([key]) => !!key)
    .map(([key, value]) => getUrlEncField(key, value))
    .join('&');
}

const GENERIC_ERROR_MESSAGE = 'Noe gikk galt. Prøv å logg inn på '
  + 'ibok igjen og prøv på nytt. '
  + 'Fungerer ikke dette ta kontakt med oss på support';

/**
 * Handles API responses from iBok.
 * Any errors generated will be on the form
 * {
 *   error: String, (should be human readable and always in norwegian)
 *   details: String, (any additional details used for developer debugging)
 *   response: Response,
 *   data: String|object (response data)
 * }
 *
 * @param {Response} res
 * */
export async function responseHandler(res) {
  const { status } = res;

  if (status === 503) {
    const error = new HttpError(
      'Tjenesten ser ut til å vere utilgjengelig',
      status,
      null,
      res.text(),
      res,
    );
    captureException(error, null, {
      'request.uri': res.url,
      'response.status': res.status,
    });
    throw error;
  }

  const contentType = res.headers.get('content-type');

  // 204 Responses should by design never return anything in the response body
  if (res.status === 204) {
    return null;
  }

  // Gracefully handle non-json response
  if (!isApplicationJson(contentType)) {
    console.error(`Response was not application/json (actual value: ${contentType}). User is probably logged out`);

    const error = new HttpError(
      GENERIC_ERROR_MESSAGE,
      res.status,
      `Response data is not application/json (actual: ${contentType})`,
      res.text(),
      res,
    );
    captureException(error, null, {
      'request.uri': res.url,
      'response.status': res.status,
    });
    throw error;
  }

  const data = await res.json();

  // Catch any custom access-errors from the server
  if (res.status === 401 || res.status === 403) {
    const title = data.errors && data.errors.global
      ? data.errors.global
      : 'Du har ikke tilgang. Prøv å logg inn på nytt igjen';

    throw new HttpError(
      title,
      res.status,
      null,
      data,
      res,
    );
  }

  if (res.status >= 500) {
    const title = data.errors && data.errors.global
      ? data.errors.global
      : GENERIC_ERROR_MESSAGE;

    const error = new HttpError(
      title,
      res.status,
      null,
      data,
      res,
    );
    captureException(
      error,
      null,
      {
        'request.uri': res.url,
        'response.status': res.status,
      },
      {
        'response.body': JSON.stringify(data),
      },
    );
    throw error;
  }

  if (res.status === 404) {
    throw new HttpError(
      // Use the title provided by the response,
      // or default to a static message
      data.title || 'Klarte ikke å hente denne ressursen',
      res.status,
      data.detail,
      res,
    );
  }

  // Catch any custom client errors from server
  if (res.status === 400 || data.errors) {
    let title;
    let isFatal = false;

    if (data.detail && !data.errors) {
      title = data.detail;
    } else if (typeof data.error === 'string') {
      // data.error could be datamapper validation errors
      title = data.error;
    // data.errors.global could be custom validation issues
    } else if (data.errors.global) {
      title = data.errors.global;
    // Missing global error, but field specific errors are available
    } else if (Object.keys(data.errors).length > 0) {
      title = 'Kunne ikke gjennomføre handlingen';
    } else {
      // We are not sure what this error is
      isFatal = true;
      title = 'Kunne ikke gjennomføre handlingen. Prøv gjerne på nytt senere. '
        + 'Fortsetter problemet, så kontakt oss gjerne på brukerstøtte';
    }

    const error = new HttpError(
      title,
      res.status,
      null,
      data,
      res,
    );
    if (isFatal) {
      captureException(
        error,
        null,
        {
          'request.uri': res.url,
          'response.status': res.status,
        },
        {
          'response.body': JSON.stringify(data),
        },
      );
    }
    throw error;
  }

  return data;
}

/**
 * Calculates the next delay (Milliseconds) in the Exponential Backoff strategy,
 * based on current number of attempts.
 * @param {number} attempt
 * @return {number}
 * */
export const exponentialBackoffDelayMs = (attempt) => (
  Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000)
);
